import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useStyles } from "../../../theme/appStyles";
import {
  useDeleteProductMutation,
  useGetBrandQuery,
  useGetCategoryQuery,
  useGetMainItemQuery,
  useGetProductQuery,
  useGetTagQuery,
  useGetPrinterQuery,
} from "../../../Api/attoDeskApi";
import {
  ICategory,
  IMainItem,
  IVariantView,
  IStore,
  IBrand,
  ITag,
  IPrinter,
} from "../../../Api/Interface/api.interface";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import NewPopupTenant from "../Edit/index";
import ProductionQuantityLimitsOutlinedIcon from "@mui/icons-material/ProductionQuantityLimitsOutlined";
import NewPopUpProduct from "../../Product/Edit/index";
import { useNotifier } from "../../../Core/Notifier";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { SizeOfLevelType } from "../../../Core/Enum/enum";

const convertUnit = (unit: number) => {
  switch (unit) {
    case SizeOfLevelType.kg:
      return "kg";
    case SizeOfLevelType.small:
      return "small";
    case SizeOfLevelType.li:
      return "li";
    case SizeOfLevelType.Medium:
      return "Medium";
    case SizeOfLevelType.Bottle:
      return "Bottle";
    case SizeOfLevelType.Each:
      return "Each";
    case SizeOfLevelType.Large:
      return "Large";
    case SizeOfLevelType.case:
      return "Case";
    default:
      return "";
  }
};

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectCustomer, setSelectCustomer] = useState<IMainItem | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<IVariantView | null>(
    null
  );
  const [openDialogProduct, setOpenDialogProduct] = useState(false);
  const [openDeleteProduct, setOpenDeleteProduct] = useState(false);
  const [productToDelete, setProductToDelete] = useState<IVariantView | null>(
    null
  );

  const { id: ProductId } = useParams<{ id: string }>();

  useEffect(() => {}, [ProductId]);

  const { data, isLoading, isError } = useGetMainItemQuery({
    searchText: "",
    id: String(ProductId),
    variantBrandId: null,
    categoryId: null,
    variantTagId: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const {
    data: productData,
    isLoading: productIsLoading,
    isError: productIsError,
  } = useGetProductQuery({
    searchText: "",
    id: null,
    variantBrandId: null,
    categoryId: null,
    variantTagId: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
    mainItemId: String(ProductId),
  });

  const { data: productBrandData, isLoading: brandIsLoading } =
    useGetBrandQuery({
      searchText: "",
      isActive: "1",
      id: "",
    });
  const { data: productTagData, isLoading: ProductTagLoading } = useGetTagQuery(
    {
      searchText: "",
      isActive: "1",
      id: "",
    }
  );

  const { data: printerData, isLoading: PrinterLoading } = useGetPrinterQuery({
    searchText: "",
  });

  const { data: categoryData, isLoading: categoryLoading } =
    useGetCategoryQuery();

  const product = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as IMainItem;
    }
    return null;
  }, [data]);

  const categoryList = useMemo(() => {
    return categoryData?.data as ICategory[];
  }, [categoryData?.data]);

  const productBrandList = useMemo(() => {
    return productBrandData?.data as IBrand[];
  }, [productBrandData?.data]);

  const productTagList = useMemo(() => {
    return productTagData?.data as ITag[];
  }, [productTagData?.data]);

  const printerList = useMemo(() => {
    return printerData?.data as IPrinter[];
  }, [printerData?.data]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    categoryList?.forEach((category) => {
      map.set(category.id, category.categoryName);
    });
    return map;
  }, [categoryList]);

  const productBrandMap = useMemo(() => {
    const map = new Map();
    productBrandList?.forEach((brand) => {
      map.set(brand.id, brand.brandName);
    });
    return map;
  }, [productBrandList]);

  const tagMap = useMemo(() => {
    const map = new Map();
    productTagList?.forEach((brand) => {
      map.set(brand.id, brand.tagName);
    });
    return map;
  }, [productTagList]);

  const printerMap = useMemo(() => {
    const map = new Map();
    printerList?.forEach((brand) => {
      map.set(brand.id, brand.printerName);
    });
    return map;
  }, [printerList]);

  const handleOpenEditTenant = () => {
    setSelectCustomer(product);
    setOpenDialog(true);
  };

  const handleCloseEditTenant = () => {
    setSelectCustomer(null);
    setOpenDialog(false);
  };

  const handleOpenDialog = (product: IVariantView) => {
    setSelectedProduct(product);
    setOpenDialogProduct(true);
  };

  const handleCloseDialog = () => {
    setSelectedProduct(null);
    setOpenDialogProduct(false);
  };

  const handleOpenDeletePopup = (product: IVariantView) => {
    setProductToDelete(product);
    setOpenDeleteProduct(true);
  };

  const handleCloseDeletePopup = () => {
    setProductToDelete(null);
    setOpenDeleteProduct(false);
  };

  const handleAddProductClick = () => {
    navigate(`/product/create/${product?.id}`);
  };

  const [deleteCategory] = useDeleteProductMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteProduct(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the product");
    }
  };

  const coursings: IVariantView[] = Array.isArray(productData?.data)
    ? (productData?.data as IVariantView[])
    : [];

  if (isLoading || categoryLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error fetching product details data. Please try again later.
      </Typography>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Card className={classes.stepperCard}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              pt={2}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                alignContent: "center",
              }}
            >
              <Typography variant="h2">{product?.mainItemName}</Typography>
            </Grid>
            <Grid
              item
              pt={2}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                alignContent: "center",
              }}
            >
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIosIcon />}
                className={classes.addButton}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12} pb={2}>
            <Card className={classes.stepperCard}>
              <Grid container justifyContent={"space-between"} spacing={2}>
                {product?.mainItemImg && (
                  <Grid item lg={3} md={4} sm={12} xs={12}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        textAlign="center"
                      >
                        <Grid
                          container
                          textAlign="center"
                          justifyContent={"center"}
                        >
                          <Grid
                            item
                            textAlign="center"
                            className={classes.SelectImageTableBoxwithBorder245}
                          >
                            {product?.mainItemImg ? (
                              <img
                                src={product?.mainItemImg}
                                alt="img"
                                // alt={row.variantName.toLowerCase()}
                                className={classes.SelectImageTable12}
                              />
                            ) : (
                              <ProductionQuantityLimitsOutlinedIcon
                                className={classes.SelectImageTable}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid
                  item
                  lg={product?.mainItemImg ? 9 : 12}
                  md={product?.mainItemImg ? 8 : 12}
                  sm={12}
                  xs={12}
                >
                  <Grid container justifyContent={"space-between"} spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={1}
                        sx={{ borderBottom: 1, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            alignContent: "center",
                          }}
                        >
                          <Typography className={classes.cardTitle}>
                            {product?.mainItemName}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Grid
                            container
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ display: "flex" }}
                          >
                            <Grid item>
                              <Button
                                startIcon={<EditSharpIcon />}
                                className={classes.addButtonPdfExcel}
                                onClick={handleOpenEditTenant}
                              >
                                Edit
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ px: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          alignContent={"center"}
                          direction={"column"}
                        >
                          <Typography variant="subtitle1">
                            <span className={classes.customerDetails}>
                              Name :
                            </span>{" "}
                            {product?.mainItemName}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    {product?.categoryId && (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ px: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            alignContent={"center"}
                            direction={"column"}
                          >
                            <Typography variant="subtitle1">
                              <span className={classes.customerDetails}>
                                Category :
                              </span>{" "}
                              {categoryMap.get(product?.categoryId) || ""}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    {product?.mainItemShortDescription && (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ px: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            alignContent={"center"}
                            direction={"column"}
                          >
                            <Typography variant="subtitle1">
                              <span className={classes.customerDetails}>
                                Short Description :
                              </span>{" "}
                              {product?.mainItemShortDescription}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    {product?.mainItemLongDescription && (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ px: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            alignContent={"center"}
                            direction={"column"}
                          >
                            <Typography variant="subtitle1">
                              <span className={classes.customerDetails}>
                                Long Description :
                              </span>{" "}
                              {product?.mainItemLongDescription}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    {product?.mainItemIsLooping == true && (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ px: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            alignContent={"center"}
                            direction={"column"}
                          >
                            <Typography variant="subtitle1">
                              <span className={classes.customerDetails}>
                                Is Looping:
                              </span>{" "}
                              Yes
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {/* {product?.mainItemIsLooping == true && (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container spacing={1}>
                          <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                            <Grid
                              container
                              justifyContent={"space-between"}
                              spacing={5}
                              sx={{ borderBottom: 2, padding: 2 }}
                              className={classes.titleGrid}
                            >
                              <Grid
                                item
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  alignContent: "center",
                                }}
                              >
                                <Typography className={classes.cardTitle}>
                                  Looping
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          {product?.mainItemIsLooping && (
                            <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                              <Card className={classes.stepperCard}>
                                <Grid
                                  container
                                  justifyContent={"space-between"}
                                  spacing={2}
                                  sx={{ padding: 2 }}
                                  className={classes.titleGrid}
                                >
                                  {product?.loopingDetails?.tableColumns.map(
                                    (column, index) => (
                                      <Grid
                                        item
                                        lg={12}
                                        md={12}
                                        sm={12}
                                        xs={12}
                                        pt={2}
                                        alignContent={"center"}
                                        key={index}
                                        className={
                                          classes.productViewPageTableHeaderCell
                                        }
                                      >
                                        <Typography>
                                          <span
                                            className={classes.customerDetails}
                                          >
                                            {column.name} :
                                          </span>{" "}
                                          {column.type === "boolean"
                                            ? column.value
                                              ? "True"
                                              : "False"
                                            : column.value}
                                        </Typography>
                                      </Grid>
                                    )
                                  )}
                                </Grid>
                              </Card>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    )} */}
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12} pb={2}>
            <Card className={classes.stepperCard}>
              <Grid container justifyContent={"space-between"} spacing={2}>
                <>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={1}
                      sx={{ borderBottom: 1, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          alignContent: "center",
                        }}
                      >
                        <Typography className={classes.cardTitle}>
                          Variant
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Grid
                          container
                          spacing={1}
                          justifyContent="flex-end"
                          sx={{ display: "flex" }}
                        >
                          <Grid item>
                            <Button
                              variant="contained"
                              className={classes.addButton}
                              onClick={handleAddProductClick}
                            >
                              Add New
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {productIsLoading ? (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={2}
                      >
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid
                            container
                            justifyContent={"space-between"}
                            spacing={1}
                            sx={{ borderBottom: 1, padding: 2 }}
                            className={classes.titleGrid}
                            alignContent={"center"}
                            textAlign={"center"}
                          >
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="10vh"
                            >
                              <CircularProgress />
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : productIsError || coursings.length === 0 ? (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={2}
                        sx={{ padding: 2, minHeight: 200 }}
                      >
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1" textAlign={"center"}>
                            {isError
                              ? "Error fetching data"
                              : "No product available"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    coursings.map((row: IVariantView, index) => (
                      <Grid item lg={12} md={12} sm={12} xs={12} key={row.id}>
                        <Grid
                          container
                          alignItems={"center"}
                          alignContent={"center"}
                          justifyContent={"center"}
                          spacing={2}
                          sx={{ borderBottom: 1 }}
                          className={classes.titleGrid}
                        >
                          <Grid item lg={2} md={4} sm={12} xs={12}>
                            <Grid
                              container
                              spacing={2}
                              alignItems={"center"}
                              alignContent={"center"}
                              justifyContent={"center"}
                            >
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Grid
                                  container
                                  justifyContent={"space-between"}
                                  spacing={1}
                                  sx={{ padding: 2 }}
                                  className={classes.titleGrid}
                                >
                                  <Grid
                                    item
                                    className={
                                      classes.SelectImageTableBoxwithBorder2456
                                    }
                                  >
                                    {row.variantImg ? (
                                      <img
                                        src={row?.variantImg}
                                        alt="img"
                                        // alt={row.variantName.toLowerCase()}
                                        className={classes.SelectImageTable12}
                                      />
                                    ) : (
                                      <ProductionQuantityLimitsOutlinedIcon
                                        className={classes.SelectImageTable}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item lg={10} md={8} sm={12} xs={12}>
                            <Grid
                              container
                              justifyContent={"space-between"}
                              spacing={2}
                            >
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Grid
                                  container
                                  justifyContent={"space-between"}
                                  spacing={1}
                                  sx={{ padding: 2 }}
                                  className={classes.titleGrid}
                                >
                                  <Grid
                                    item
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-start",
                                      alignItems: "center",
                                      alignContent: "center",
                                    }}
                                  >
                                    <Typography className={classes.cardTitle}>
                                      {row?.variantName}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Grid
                                      container
                                      spacing={1}
                                      justifyContent="flex-end"
                                      sx={{ display: "flex" }}
                                    >
                                      <Grid item>
                                        <Button
                                          // variant="contained"
                                          className={classes.editButtonList}
                                          onClick={() => handleOpenDialog(row)}
                                        >
                                          <EditSharpIcon
                                            className={classes.editListIcon}
                                          />
                                        </Button>
                                      </Grid>
                                      <Grid item>
                                        <Button
                                          // variant="contained"
                                          className={classes.deleteButtonList}
                                          onClick={() =>
                                            handleOpenDeletePopup(row)
                                          }
                                        >
                                          <DeleteOutlineSharpIcon
                                            className={classes.deleteListIcon}
                                          />
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Grid
                                  container
                                  justifyContent={"space-between"}
                                  spacing={5}
                                  sx={{ px: 2, pb: 3 }}
                                  className={classes.titleGrid}
                                >
                                  <Grid
                                    item
                                    lg={4}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    // alignContent={"center"}
                                    direction={"column"}
                                  >
                                    <Typography variant="subtitle1">
                                      <span className={classes.customerDetails}>
                                        Name :
                                      </span>{" "}
                                      {row?.variantName}
                                    </Typography>
                                    {row?.variantDisplayName && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Display Name :
                                        </span>{" "}
                                        {row?.variantDisplayName}
                                      </Typography>
                                    )}
                                    {row?.variantBrandId && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Brand :
                                        </span>{" "}
                                        {productBrandMap.get(
                                          row?.variantBrandId
                                        ) || ""}
                                      </Typography>
                                    )}
                                    {row?.variantTagId.length > 0 && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Tags :
                                        </span>{" "}
                                        {row.variantTagId
                                          .map((id) => tagMap.get(id) || "")
                                          .join(", ")}
                                      </Typography>
                                    )}
                                    {row?.variantPrinterIds.length > 0 && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Printer :
                                        </span>{" "}
                                        {row.variantPrinterIds
                                          .map((id) => printerMap.get(id) || "")
                                          .join(", ")}
                                      </Typography>
                                    )}
                                    {row?.categoryId && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Category :
                                        </span>{" "}
                                        {categoryMap.get(row?.categoryId) || ""}
                                      </Typography>
                                    )}
                                    {row?.variantUnit && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Conversion unit :
                                        </span>{" "}
                                        ({convertUnit(row.variantUnit)})
                                      </Typography>
                                    )}
                                    {row?.packCount && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Pack Count :
                                        </span>{" "}
                                        {row?.packCount}
                                      </Typography>
                                    )}

                                    {row?.size && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Size :
                                        </span>{" "}
                                        {row?.size}
                                      </Typography>
                                    )}
                                  </Grid>

                                  <Grid
                                    item
                                    lg={4}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    // alignContent={"center"}
                                    direction={"column"}
                                  >
                                    {row?.cost && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Cost :
                                        </span>{" "}
                                        {row.cost !== null
                                          ? `$${row.cost}`
                                          : ""}
                                      </Typography>
                                    )}
                                    {row?.regularPrice && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Regular Price :
                                        </span>{" "}
                                        {row.regularPrice !== null
                                          ? `$${row.regularPrice}`
                                          : ""}
                                      </Typography>
                                    )}
                                    {row?.salePrice && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Sale Price :
                                        </span>{" "}
                                        {row.salePrice !== null
                                          ? `$${row.salePrice}`
                                          : ""}
                                      </Typography>
                                    )}
                                    {row?.orderNumber && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Order Number :
                                        </span>{" "}
                                        {row?.orderNumber}
                                      </Typography>
                                    )}

                                    {row?.isActive == true && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Active :
                                        </span>{" "}
                                        Yes
                                      </Typography>
                                    )}
                                    {row?.isOnSale == true && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Is Sale :
                                        </span>{" "}
                                        Yes
                                      </Typography>
                                    )}
                                    {row?.isPosDisplay == true && (
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Is Pos Display :
                                        </span>{" "}
                                        Yes
                                      </Typography>
                                    )}
                                    {row?.bgColor && (
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Button Color:
                                        </span>
                                        <Box
                                          sx={{
                                            width: 35,
                                            height: 35,
                                            backgroundColor: row.bgColor,
                                            border: "1px solid #d3d3d3",
                                            borderRadius: "8px",
                                          }}
                                        />
                                      </Typography>
                                    )}
                                  </Grid>

                                  {row?.isLooping == true && (
                                    <Grid
                                      item
                                      lg={4}
                                      md={6}
                                      sm={12}
                                      xs={12}
                                      direction={"column"}
                                    >
                                      <Typography variant="subtitle1">
                                        <span
                                          className={classes.customerDetails}
                                        >
                                          Looping :
                                        </span>{" "}
                                        {row?.loopingDetails?.shortTableName}
                                      </Typography>

                                      {row?.loopingDetails?.tableColumns &&
                                        row.loopingDetails.tableColumns.filter(
                                          (column) => column.value !== null
                                        ).length > 0 && (
                                          <>
                                            {row.loopingDetails.tableColumns
                                              .filter(
                                                (column) =>
                                                  column.value !== null
                                              )
                                              .map((column, index) => (
                                                <Typography
                                                  key={index}
                                                  variant="subtitle1"
                                                >
                                                  <span
                                                    className={
                                                      classes.customerDetails
                                                    }
                                                  >
                                                    {column.name} :
                                                  </span>{" "}
                                                  {column.type === "boolean"
                                                    ? column.value
                                                      ? "True"
                                                      : "False"
                                                    : column.value}
                                                </Typography>
                                              ))}
                                          </>
                                        )}
                                    </Grid>
                                  )}

                                  {(row?.variantShortDescription ||
                                    row?.variantLongDescription) && (
                                    <Grid
                                      item
                                      lg={12}
                                      md={12}
                                      sm={12}
                                      xs={12}
                                      // alignContent={"center"}
                                      direction={"column"}
                                    >
                                      {row?.variantShortDescription && (
                                        <Typography variant="subtitle1">
                                          <span
                                            className={classes.customerDetails}
                                          >
                                            Short Description :
                                          </span>{" "}
                                          {row?.variantShortDescription}
                                        </Typography>
                                      )}
                                      {row?.variantLongDescription && (
                                        <Typography variant="subtitle1">
                                          <span
                                            className={classes.customerDetails}
                                          >
                                            Long Description :
                                          </span>{" "}
                                          {row?.variantLongDescription}
                                        </Typography>
                                      )}
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))
                  )}
                </>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      {selectCustomer && (
        <NewPopupTenant
          openModel={openDialog}
          handleCloseDialog={handleCloseEditTenant}
          data={product ?? undefined}
        />
      )}
      {selectedProduct && (
        <NewPopUpProduct
          openModel={openDialogProduct}
          handleCloseDialog={handleCloseDialog}
          data={selectedProduct}
        />
      )}
      {productToDelete && (
        <DeletePopup
          open={openDeleteProduct}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(productToDelete.id.toString());
          }}
          title="Delete Product"
          content={`Are you sure you want to delete "${productToDelete.variantName}"?`}
        />
      )}
    </Grid>
  );
};

export default Index;
